import { useCallback, useEffect, useState } from 'react'
import type { CoachHealthSummary } from './coachHealthTypes'
import { supabase } from './supabaseClient'

type UseCoachHealthSummaryOptions = {
  enabled?: boolean
}

export function useCoachHealthSummary({ enabled = true }: UseCoachHealthSummaryOptions = {}) {
  const [summary, setSummary] = useState<CoachHealthSummary | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [connecting, setConnecting] = useState(false)

  const fetchSummary = useCallback(async (sync = false) => {
    if (!enabled) {
      setSummary(null)
      setLoading(false)
      setConnecting(false)
      return
    }

    setLoading(true)
    try {
      const { data, error } = await supabase.functions.invoke('get-health-summary', {
        body: { sync },
      })
      if (error) throw error
      if (data?.error) throw new Error(String(data.error))
      setSummary(data as CoachHealthSummary)
    } catch (err) {
      console.error('Health summary fetch error:', err)
    } finally {
      setLoading(false)
      setConnecting(false)
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) {
      setSummary(null)
      setLoading(false)
      setConnecting(false)
      return
    }

    const params = new URLSearchParams(window.location.search)
    const justConnected = params.get('connected') === 'true'

    void fetchSummary(true)

    if (justConnected) {
      const retryTimers = [5000, 15000, 30000, 60000].map((delay) =>
        window.setTimeout(() => void fetchSummary(true), delay),
      )
      params.delete('connected')
      const tab = params.get('tab') ?? 'coach'
      const next = params.toString() ? `/app?${params.toString()}` : `/app?tab=${tab}`
      window.history.replaceState({}, '', next)

      return () => retryTimers.forEach((timer) => window.clearTimeout(timer))
    }
  }, [enabled, fetchSummary])

  return {
    summary,
    loading,
    connecting,
    setConnecting,
    fetchSummary,
  }
}
